import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { api, resolveMediaUrl } from "../../lib/api";

export const News = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/news")
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <section
      id="news"
      data-testid="news-section"
      className="bg-white py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#0D2F4C]" />
            <span
              data-testid="news-overline"
              className="text-xs uppercase tracking-[0.3em] font-bold text-[#0D2F4C]"
            >
              {t.news.overline}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="news-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.news.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[#4B5563] text-lg leading-relaxed"
          >
            {t.news.subtitle}
          </motion.p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[420px] bg-[#F8F9FA] animate-pulse"
                data-testid={`news-skeleton-${i}`}
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="news-empty"
            className="text-center py-20 text-[#4B5563] border border-[#0D2F4C]/10"
          >
            {t.news.empty}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            data-testid="news-grid"
          >
            {items.slice(0, 6).map((item, i) => (
              <motion.article
                key={item.id}
                data-testid={`news-card-${item.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                className="group flex flex-col"
              >
                <Link
                  to={`/news/${item.id}`}
                  data-testid={`news-card-link-${item.id}`}
                  className="flex flex-col cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#F8F9FA] mb-6">
                    {item.image_url ? (
                      <img
                        src={resolveMediaUrl(item.image_url)}
                        alt={lang === "fr" ? item.title_fr : item.title_en}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0D2F4C]/10 to-[#0D2F4C]/30" />
                    )}
                    {item.video_url && (
                      <div className="absolute top-3 right-3 bg-[#0D2F4C] text-white text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-white rounded-full inline-block" />
                        Vidéo
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0D2F4C]">
                      {lang === "fr" ? item.category_fr : item.category_en}
                    </span>
                    <span className="h-px flex-1 bg-[#0D2F4C]/15" />
                    <span className="text-xs text-[#4B5563]">
                      {formatDate(item.created_at)}
                    </span>
                  </div>

                  <h3
                    className="font-serif text-2xl text-[#0D2F4C] leading-tight mb-3 group-hover:opacity-70 transition-opacity duration-300"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {lang === "fr" ? item.title_fr : item.title_en}
                  </h3>

                  <p className="text-sm text-[#4B5563] leading-relaxed mb-5 flex-1">
                    {lang === "fr" ? item.excerpt_fr : item.excerpt_en}
                  </p>

                  <div className="flex items-center gap-2 text-sm font-medium text-[#0D2F4C] group-hover:gap-3 transition-all duration-300">
                    <span>{t.news.read_more}</span>
                    <ArrowUpRight size={16} />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
